import { TAnyCodec, TCodec, TTypeOfCodec, formStatus, TTypeOfNewDefault, TAnyRecordOfStrings } from "../codec";
import { either } from "fp-ts/lib";
import { TError, TErrorTuple } from "../errors";
import { required } from "./required";
import { pipe } from "fp-ts/lib/function";
import { unknown } from "./unknown";
import { array } from "./array";
import { tuple } from "./tuple";
import { errorCode } from "./errorCode";
import { string } from "./string";
import { intersection } from "./intersection";
import { partial } from "./partial";

export const FileIOSailKeyPrefix = "x-sail-";

export type TAnyFileIOCodec = TCodec<
    "FileIOCodec",
    // TAnyRecordOfStrings limits the definition of this codec to only be a 1 level object of strings. This is because
    // this codec is used for file uploads where the input data is included in the headers, which without complicating things
    // does not support nesting or anything other than string inputs
    {input: TAnyRecordOfStrings; output: TAnyCodec},
    TFileIO<TTypeOfCodec<TAnyRecordOfStrings>, unknown>,
    TFileIO<TTypeOfCodec<TAnyRecordOfStrings>, unknown>
>;

export type TFileIO<I, O> = {
    status: TTypeOfCodec<typeof formStatus>;
    input: I;
    output: O;
    clientSideFileForUpload?: File;
    validationErrors: TError;
};

const validationErrorsCodec = array(tuple([errorCode(), string()]));

export const fileIOToRequiredCodec = <C extends TAnyFileIOCodec>(codec: C) => required({
    status: formStatus,
    input: codec.payload.input,
    output: codec.payload.output,
    validationErrors: validationErrorsCodec,
});

export type TFileIOCodec<
    I extends TAnyRecordOfStrings,
    O extends TAnyCodec
> = TCodec<"FileIOCodec", { input: I; output: O }, TFileIO<TTypeOfCodec<I>, TTypeOfCodec<O>>, TFileIO<TTypeOfNewDefault<I>, TTypeOfNewDefault<O>>>

export const fileIO = <
    I extends TAnyRecordOfStrings,
    O extends TAnyCodec,
    D extends TFileIO<TTypeOfCodec<I>, TTypeOfCodec<O>>,
    N extends TFileIO<TTypeOfNewDefault<I>, TTypeOfNewDefault<O>>
>(input: I, output: O): TCodec<"FileIOCodec", { input: I; output: O }, D, N> => {
    const formCodec = intersection([
        required({
            status: formStatus,
            input: unknown(),
            validationErrors: validationErrorsCodec,
        }),
        partial({
            output: unknown(),
            // clientSideFileForUpload would be included here but decode never includes it as when decoding it should never exist
        }),
    ]);

    return ({
        type: "FileIOCodec",
        payload: {input, output},
        decode: (i: unknown): either.Either<TError, D> => {
            const decoded = formCodec.decode(i);
            return pipe(
                decoded,
                either.fold(
                    (e) => // Try to decode the body as both the input and the output if so create a fileIO out of it else left
                        pipe(
                            input.decode(i),
                            either.mapLeft(() => e),
                            either.chain((decodedInput) => pipe(
                                output.decode(i),
                                either.fold(
                                    () => either.left(e),
                                    (decodedOutput) => either.right({...formCodec.newDefault(), ...{input: decodedInput}, ...{output: decodedOutput}} as D)
                                )
                            ))
                        ),
                    (d) => pipe(
                        input.decode(d.input),
                        either.mapLeft((e) => e.map(([err, c]) => [err, `input.${c}`] as TErrorTuple)),
                        either.chain((decodedInput) => pipe(
                            output.decode(d.output),
                            either.fold(
                                () => either.right({...d, ...{input: decodedInput}, ...{output: output.newDefault()}} as D),
                                (decodedOutput) => either.right({...d, ...{input: decodedInput}, ...{output: decodedOutput}} as D)
                            )

                        )),
                    ),
                )
            );
        },
        decodeNewDefault: (i: unknown): either.Either<TError, N> => {
            const decoded = formCodec.decodeNewDefault(i);
            return pipe(
                decoded,
                either.fold(
                    (e) => // Try to decode the body as both the input and the output if so create a fileIO out of it else left
                        pipe(
                            input.decodeNewDefault(i),
                            either.mapLeft(() => e),
                            either.chain((decodedInput) => pipe(
                                output.decodeNewDefault(i),
                                either.fold(
                                    () => either.left(e),
                                    (decodedOutput) => either.right({...formCodec.newDefault(), ...{input: decodedInput}, ...{output: decodedOutput}} as N)
                                )
                            ))
                    ),
                    (d) => pipe(
                        input.decodeNewDefault(d.input),
                        either.mapLeft((e) => e.map(([err, c]) => [err, `data.${c}`] as TErrorTuple)),
                        either.chain((decodedInput) => pipe(
                            output.decodeNewDefault(d.output),
                            either.fold(
                                () => either.right({...d, ...{input: decodedInput}, ...{output: output.newDefault()}} as N),
                                (decodedOutput) => either.right({...d, ...{input: decodedInput}, ...{output: decodedOutput}} as N)
                            )

                        )),
                    ),
                )
            );
        },
        newDefault: (): N =>
            ({...formCodec.newDefault(), ...{input: input.newDefault()}, ...{output: output.newDefault()}}) as N, // clientSideFileForUpload is not included as it is optional
    });
};
